import { observable, action, computed } from "mobx";
import { createTransformer } from "mobx-utils";
import { BaseDict } from "../dict";
import { PulseBetsTypes } from "../../../data/enums";
import { PageMethodsComposite, EventModel, PulseBettingStore } from "../../../data/store";
import { StakeCurrencyContainer } from "./StakeCurrencyContainer";
import { AcceptedBetModel } from "./AcceptedBetModel";
import { EventErrorModel } from "./EventErrorModel";
import { BetPlacementResultType } from "./BetPlacementResultType";
import { BetPlacementStatus } from "./enums/BetPlacementStatus";
import { BetPlacementModel } from "./BetPlacementModel";
import { DeviceDetect, UserInfo } from "sb-resp-lib";
import { CalculatedPeriodHelper } from "../../../data/helpers";
import { Parser, PulseBetMarketComponentData } from "../../../data";

export type DataComposite = { userInfo: UserInfo } & PageMethodsComposite;
export type FreeBetDispatchData = { marketTypeId: string, freeBetId: string }
export class PulseBetMarketsData {

    @observable timer!: number;
    @observable periodStatus!: string;
    @observable isUserLogged!: boolean;
    @observable isPlacingBetsDisabled!: boolean;
    @observable isDanger: boolean;
    @observable eventsBetPlacementResult: BetPlacementModel[];

    public store: PulseBettingStore;

    public branchId: string;
    public eventsManager: EventsManager;
    public oddsUtil: Odds;
    public provider: DataComposite;
    public userInfo: UserInfo;
    public navHandler: any;
    public betSlipUtil: BetSlipUtil;
    public deviceDetect: DeviceDetect;
    public eventsAutoCloseTimeout: any[];
    private stakeCurrencies: StakeCurrencyContainer[];
    private dict: BaseDict;
    private name: string;
    private timeoutConstantForAutoClose: number;
    protected calculatedPeriodHelperDependency: CalculatedPeriodHelper;

    constructor(provider: DataComposite, dict: BaseDict, cmsConfig: any, oddsUtil: Odds, betSlipUtil: BetSlipUtil, navHandler: any) {
        this.provider = provider;
        this.name = "PulseBetsMarket";
        this.userInfo = provider.userInfo;
        this.navHandler = navHandler;
        this.eventsManager = new EventsManager();
        this.branchId = "1";
        this.store = PulseBettingStore.getInstance;
        this.store.dataComposite = provider;
        this.dict = dict;
        this.stakeCurrencies = cmsConfig.stakeSeries;
        this.userInfo.onLogin[this.name] = this.onLogin;
        this.userInfo.onLogout[this.name] = this.onLogout;
        this.isDanger = false;
        this.isPlacingBetsDisabled = false;
        this.calculatedPeriodHelperDependency = CalculatedPeriodHelper.getInstance();
        this.userInfo.current ? this.onLogin() : this.onLogout();
        this.eventsBetPlacementResult = [];
        this.oddsUtil = oddsUtil;
        this.betSlipUtil = betSlipUtil;
        this.eventsManager.addEventListener("onMasterEventDanger", this, this.onMasterEventDanger, true);
        this.eventsManager.addEventListener("onIsPlacingBetsDisabledChanged", this, this.onIsPlacingBetsDisabledChanged, true);
        this.eventsManager.addEventListener("onMarketsDanger", this, this.onMarketsDanger, true);
        this.store.currency = this.currency;
        this.store.oddsUtil = this.oddsUtil;
        this.store.betslipUtil = this.betSlipUtil;
        this.store.isUserLogged = this.isUserLogged;
        this.store.navHandler = navHandler;
        this.eventsAutoCloseTimeout = [];
        this.timeoutConstantForAutoClose = cmsConfig.autoCloseTimeout;
        this.deviceDetect = DeviceDetect.getInstance();
    }

    /**
    *  Gets the text for the stake change link
    * */
    stakeTypeText = createTransformer((marketTypeId: string) => {
        let stakeText: string = ""

        let market = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.typeId === marketTypeId);
        if (market) {
            stakeText = market.freeBetsEnabled ? this.dict.UseMyBalance : this.dict.UseFreeBets;
        }

        return stakeText;
    });

    @action setParams(masterEventId: string, eventIds: EventModel[], branchID: string) {
        if (this.store.eventIds.length === eventIds.length) {
            return;
        }
        this.eventsBetPlacementResult = [];
        this.store.setParams(masterEventId, eventIds);
        this.branchId = branchID;

        this.initAllBetPlacementModel(eventIds);
        this.eventsManager.addEventListener("onPulseBetPlacementResult", this, this.onPulseBetPlacementResult, true);
        this.eventsManager.addEventListener("onSelectFreeBetFromBetSlip", this, this.onSetFreeBet, true);
        this.eventsManager.addEventListener("onSelectFreeBetFromPulse", this, this.onSetFreeBet, true);
        this.eventsManager.addEventListener("onSetStake", this, this.onSetStake, true);
        this.userInfo.onDataUpdated[this.name] = this.onSetFreeBet;
    }

    @action.bound onSetFreeBet() {
        for (let eventInfo of this.store.events) {
            let availableFreeBetsData = this.betSlipUtil.getAvailableFreeBetsForPulse(eventInfo);
            let bonusInfo = Parser.BonusInfoParser(availableFreeBetsData);
            let pulseBetMarket = this.store.pulsebetsDataForComponents.find(x => x.freePulseBets.marketTypeId === eventInfo.typeId);
            if (pulseBetMarket) {
                pulseBetMarket.updateFreePulseBets(bonusInfo);
            }
        }
    }

    stakeInputDataView = createTransformer((eventId: string) => {
        let pulseBetdata = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        if (pulseBetdata) {
            return {
                stake: pulseBetdata.stakeInputData.stakeValue,
                eventId,
                stakeFormatted: this.getUserStakeFormatted(pulseBetdata.stakeInputData.stakeValue),
                isMinusDisabled: this.isMinusDisabled(eventId) || false,
                isPlusDisabled: this.isPlusDisabled(eventId) || false,
                stakeDown: this.stakeDown,
                stakeUp: this.stakeUp
            }
        }

        return {
            stake: this.store.pulsebetsDataForComponents[0].stake,
            eventId,
            stakeFormatted: this.getUserStakeFormatted(this.store.pulsebetsDataForComponents[0].stakeInputData.stakeValue),
            isMinusDisabled: this.isMinusDisabled(eventId) || false,
            isPlusDisabled: this.isPlusDisabled(eventId) || false,
            stakeDown: this.stakeDown,
            stakeUp: this.stakeUp
        }
    });

    getUserStakeFormatted(stake: number) {
        return this.isUserLogged ? this.calculatedPeriodHelperDependency.formatWinnings(stake) :
            stake.toString();
    }

    @action.bound onSetStake(data) {
        let pulseBetMarket = this.store.pulsebetsDataForComponents.find(x => x.freePulseBets.marketTypeId === data.marketTypeId);
        if (pulseBetMarket) {
            let ev = this.store.events.find(event => event.id === (pulseBetMarket && pulseBetMarket.pulseBetMetadata.marketId));
            if (ev) {
                pulseBetMarket.externalPlaceBetHandler = this.store.getPulseBetMarketHandler(this.isUserLogged, ev, pulseBetMarket.stakeInputData.stakeValue);
            }
        }
    }

    deactivate() {
        this.eventsManager.removeEventListener("onPulseBetPlacementResult", this, true);
        this.eventsManager.removeEventListener("onMasterEventDanger", this, true);
        this.eventsManager.removeEventListener("onIsPlacingBetsDisabledChanged", this, true);
        this.eventsManager.removeEventListener("onMarketsDanger", this, true);
        this.eventsManager.removeEventListener("onSelectFreeBetFromBetSlip", this, true);
        this.eventsManager.removeEventListener("onSelectFreeBetFromPulse", this, true);
        this.eventsManager.removeEventListener("onSetStake", this, true);
        this.store.clear();
        this.betSlipUtil.removeAllSelectedFreePulseBets();
        this.eventsBetPlacementResult = [];
    }

    @computed get currency() {
        return this.isUserLogged ? this.getUserCurrency() : this.getDefaultCurrency();
    }

    /**
     * @function  onLogin  executed on Login of customer
     */
    @action.bound onLogin() {
        this.isUserLogged = true;
        this.store.currencySign = this.userInfo.current.currencyCode;
        this.store.isUserLogged = true;
        this.calculatedPeriodHelperDependency.setCurrentUserInfo(this.userInfo.current);
        if (this.userInfo && this.userInfo.current && this.userInfo.current.IsPlacingBetsDisabled) {
            this.isPlacingBetsDisabled = true;
        }
    }

    addSuffix(eventId: string) {
        let event = this.store.events.find(ev => ev.id === eventId && (ev.typeId === PulseBetsTypes.ThirtySecPulse || ev.typeId === PulseBetsTypes.OneMinPulse));
        if (event) {
            return this.dict.Sec;
        }
        return "";
    }
    /**
     * @function  onLogout execute on Logout of customer
     */
    @action.bound onLogout() {
        this.isUserLogged = false;
        this.store.currencySign = "";
        this.store.isUserLogged = false;
        this.calculatedPeriodHelperDependency.setCurrentUserInfo(undefined);
    }
    /**
    *  Gets the text for the place bet button
    */
    placeButtonText = createTransformer((marketTypeId: string) => {
        let market = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.typeId === marketTypeId);
        if (this.isDanger || market && market.pulseBetMetadata.isDanger) {
            return this.dict.Suspended;
        }

        return this.isUserLogged ? this.dict.PlaceBet : this.dict.PleaseLoginToPlaceBet;
    });

    /**
     * gets the default currency if there is no default it gets the first
     */
    getDefaultCurrency(): StakeCurrencyContainer {
        let container = this.stakeCurrencies.find(currency => currency.isDefault) || this.stakeCurrencies[0];
        return container;
    }

    /**
     *  gets the StakeCurrencyContainer of the user
     */
    getUserCurrency(): StakeCurrencyContainer {
        return this.stakeCurrencies.find(currency => currency.id === this.userInfo.current.currencyId) || this.getDefaultCurrency();
    }

    @action setWining(eventId: string) {
        let pulseBetdata = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        if (pulseBetdata) {
            pulseBetdata.winings.winnings = pulseBetdata.wrappedCalculatedPeriods.map((period, index) => (
                {
                    winningValue: period.formatedWin,
                    winningStatus: period.periodProgressState ? period.periodProgressState.progressStatus : undefined,
                    periodNumber: index,
                }
            ));
            pulseBetdata.onStakeChangeFading = false;
            this.store.triggerStakeWinningsAnimation(eventId);
        }
    }

    /**
        *
        * @param eventId  of the market you increase the stake
    */
    @action.bound stakeUp(eventId: string) {
        let pulseBetData = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        if (pulseBetData) {
            let index = this.currency.pulseBettingStakeValuesSeries.findIndex(stake => stake === (pulseBetData && pulseBetData.stakeInputData.stakeValue));
            pulseBetData.stakeInputData.stakeValue = this.currency.pulseBettingStakeValuesSeries[++index];
            pulseBetData.stakeInputData.index = index;

            this.isPlusDisabled(eventId);
            this.isMinusDisabled(eventId);
            this.setWining(eventId);
            let ev = this.store.events.find(evnt => evnt.id === eventId);
            if (ev) {
                pulseBetData.externalPlaceBetHandler = this.store.getPulseBetMarketHandler(this.isUserLogged, ev, pulseBetData.stakeInputData.stakeValue);
            }
        }
    }
    /**
         *
         * @param eventId  of the market is - disabled
    */
    isMinusDisabled(eventId: string) {
        let pulseBetdata = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        return pulseBetdata && pulseBetdata.stakeInputData.index <= 0 || false;
    }
    /**
        *
        * @param eventId  of the market is + disabled
    */
    isPlusDisabled(eventId: string) {
        let pulseBetdata = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        return pulseBetdata && pulseBetdata.stakeInputData.index === this.currency.pulseBettingStakeValuesSeries.length - 1 || false;
    }

    /**
     *
     * @param eventId  of the market you decrease the stake
     */
    @action.bound stakeDown(eventId: string) {
        let pulseBetData = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        if (pulseBetData) {
            let index = this.currency.pulseBettingStakeValuesSeries.findIndex(stake => stake === (pulseBetData && pulseBetData.stakeInputData.stakeValue));
            pulseBetData.stakeInputData.stakeValue = this.currency.pulseBettingStakeValuesSeries[--index];
            pulseBetData.stakeInputData.index = index;

            this.isPlusDisabled(eventId);
            this.isMinusDisabled(eventId);
            this.setWining(eventId);
            let ev = this.store.events.find(evnt => evnt.id === eventId);
            if (ev) {
                pulseBetData.externalPlaceBetHandler = this.store.getPulseBetMarketHandler(this.isUserLogged, ev, pulseBetData.stakeInputData.stakeValue);
            }
        }
    }

    @action.bound onMasterEventDanger(dangerResponse) {
        if (dangerResponse === true || dangerResponse === false) {
            this.isDanger = dangerResponse;
        }
    }

    @action.bound onIsPlacingBetsDisabledChanged(isPlacingBetsDisabled) {
        if (isPlacingBetsDisabled === true || isPlacingBetsDisabled === false) {
            this.isPlacingBetsDisabled = isPlacingBetsDisabled;
        }
    }

    @action.bound onMarketsDanger(dangerResponse) {
        for (let event of this.store.pulsebetsDataForComponents) {
            let market = dangerResponse.find(function (m) { return m.id === event.pulseBetMetadata.typeId; });
            event.pulseBetMetadata.isDanger = market.isDanger;
        }
    }

    /**
     * @function isEventIdExistInStore check if an event id is in the eventIds array in the store
     *
     * @param currentEventId string which is the ID of the event
     */
    isEventIdExistInStore(currentEventId: string) {
        return this.store.eventIds.some(mod => mod.id === currentEventId);
    }

    /**
     * @function closeEventError function called when the close button of the error message component is clicked
     *
     * @param eventId Id of pulse bet market(event)
     */
    @action.bound closeEventError(eventId: string) {
        let currentEvent = this.eventsBetPlacementResult.find(event => event.eventId === eventId);
        if (currentEvent) {
            currentEvent.status = BetPlacementStatus.NotStarted;
            currentEvent.error.message = "";
        }
    }

    goToMyPulseBet() {
        this.eventsManager.dispatchEvent("onOpenMyPulseBet", this, true);
    }

    getAcceptBetModel(ticketId: string, timeInterval: string, eventId: string) {
        return new AcceptedBetModel({
            ticketId,
            message: this.dict.BetWasAccepted,
            ticketLabel: this.dict.TicketLabel,
            timeInterval,
            buttonLabel: this.dict.GoToMyPulseBets,
            handler: this.goToMyPulseBet.bind(this),
            areaHandler: this.setBetPlacementStatusToNotStarted.bind(this, eventId)
        });
    }

    getEventErrorModel(errorMessage: string, eventId: string) {
        return new EventErrorModel({
            message: errorMessage,
            handler: this.closeEventError.bind(this, eventId)
        });
    }

    setSuccessfulBetPlacementResult(result: BetPlacementResultType, eventBetPlacementResult: BetPlacementModel) {
        eventBetPlacementResult.status = BetPlacementStatus.Success;
        eventBetPlacementResult.success.ticketId = result.ticketId || "";
        eventBetPlacementResult.success.timeInterval = result.timeInterval || "";
        eventBetPlacementResult.error.message = "";
    }

    @action setBetPlacementStatusToNotStarted(eventId: string) {
        clearTimeout(this.eventsAutoCloseTimeout[eventId]);
        let pulsebetsDataForComponents = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === eventId);
        let eventBetPlacementResult = this.eventsBetPlacementResult.find(bet => bet.eventId === eventId);
        if (pulsebetsDataForComponents && eventBetPlacementResult) {
            this.setBetPlacementModel({
                status: BetPlacementStatus.NotStarted,
                eventId,
            }, eventBetPlacementResult)
        }
    }
    checkStatusForNotStartedAndAddTimerForAutoClose(status: BetPlacementStatus, eventId: string) {
        if (BetPlacementStatus.Success === status) {
            this.eventsAutoCloseTimeout[eventId] = setTimeout(this.setBetPlacementStatusToNotStarted.bind(this, eventId), this.timeoutConstantForAutoClose);
        }
    }
    setBetPlacementModel(result: BetPlacementResultType, eventBetPlacementResult: BetPlacementModel) {
        switch (result.status) {
            case BetPlacementStatus.NotStarted:
                eventBetPlacementResult.status = BetPlacementStatus.NotStarted;
                break;

            case BetPlacementStatus.InProcess:
                eventBetPlacementResult.status = BetPlacementStatus.InProcess;
                eventBetPlacementResult.error.message = "";
                break;

            case BetPlacementStatus.Error:
                eventBetPlacementResult.status = BetPlacementStatus.Error;
                eventBetPlacementResult.error.message = result.errorMessage || "";
                break;

            case BetPlacementStatus.Success:
                this.setSuccessfulBetPlacementResult(result, eventBetPlacementResult);
                this.eventsManager.dispatchEvent("onSuccessfullyPlacedBet", this, true);
                break;

        }
    }

    initBetPlacementModel(eventId: string) {
        return new BetPlacementModel({
            eventId,
            status: BetPlacementStatus.NotStarted,
            error: this.getEventErrorModel("", eventId),
            success: this.getAcceptBetModel("", "", eventId)
        });
    }

    initAllBetPlacementModel(eventIds: EventModel[]) {
        for (let item of eventIds) {
            this.eventsBetPlacementResult.push(this.initBetPlacementModel(item.id));
        }
    }

    @action onPulseBetPlacementResult(result: BetPlacementResultType) {
        if (this.isEventIdExistInStore(result.eventId)) {
            let eventBetPlacementResult = this.eventsBetPlacementResult.find(mod => mod.eventId === result.eventId);

            if (!eventBetPlacementResult) {
                this.eventsBetPlacementResult.push(new BetPlacementModel({
                    eventId: result.eventId,
                    status: result.status,
                    error: this.getEventErrorModel(result.errorMessage || "", result.eventId),
                    success: this.getAcceptBetModel(result.ticketId || "", result.timeInterval || "", result.eventId)
                }));
            }
            else {
                this.setBetPlacementModel(result, eventBetPlacementResult);
                this.checkStatusForNotStartedAndAddTimerForAutoClose(result.status, result.eventId);
            }
            this.shouldSwitchToStakeBox(result.eventId, result.status);
        }
    }

    shouldSwitchToStakeBox(marketId: string, status: BetPlacementStatus) {
        let market = this.store.pulsebetsDataForComponents.find(data => data.pulseBetMetadata.marketId === marketId);

        if (!market || market.freePulseBets.selectedFreeBetId) { return; }

        if (status === BetPlacementStatus.Success) {
            let defaultIndex = this.currency.pulseBettingStakeValuesSeries.indexOf(this.currency.defaultStake)
            this.changeStakeValue(market, marketId, defaultIndex);
            this.switchToStakeBox(market);
        }
        else if (status === BetPlacementStatus.Error) {
            market.freeBetsEnabled && this.changeStakeValue(market, marketId, market.stakeInputData.index);
            this.switchToStakeBox(market);
        }
    }

    changeStakeValue(market: PulseBetMarketComponentData, marketId: string, index: number) {
        market.stakeInputData.index = index;
        market.stakeInputData.stakeValue = this.currency.pulseBettingStakeValuesSeries[index];
        let ev = this.store.events.find(evnt => evnt.id === marketId);
        if (ev) {
            market.externalPlaceBetHandler = this.store.getPulseBetMarketHandler(this.isUserLogged, ev, market.stakeInputData.stakeValue);
        }
        this.isMinusDisabled(marketId);
        this.isPlusDisabled(marketId);
    }

    switchToStakeBox(market: PulseBetMarketComponentData) {
        market.freeBetsEnabled = false;
    }

    IsSeamlessOrWallet(): boolean {
        return this.navHandler.IsSeamlessOrWallet();
    }
}
